<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import WahaSessionsAPI from 'dashboard/api/wahaSessions';
import InboxesAPI from 'dashboard/api/inboxes';
import WahaCallsAPI from 'dashboard/api/channel/whatsapp/wahaCallsAPI';
import SettingsToggleSection from 'dashboard/components-next/Settings/SettingsToggleSection.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import TextArea from 'next/textarea/TextArea.vue';
import CallRecordingSettings from './CallRecordingSettings.vue';

// WhatsApp voice calls of a WAHA inbox: answered and placed from the agent browser through
// the WAHA server (GOWS engine), like the WhatsApp Cloud calling inboxes.
const props = defineProps({
  inbox: {
    type: Object,
    required: true,
  },
});

const store = useStore();
const { t } = useI18n();

const isToggling = ref(false);
const isChecking = ref(false);
const health = ref(null);
const busyMessage = ref('');
const isSavingBusyMessage = ref(false);

const voiceEnabled = computed(() => Boolean(props.inbox.voice_enabled));
const sessionId = computed(() => props.inbox.waha_session?.id);
// The inbox sends the default text until it is changed; blank means no message
const busyMessageEnabled = computed(() =>
  Boolean(props.inbox.call_busy_message)
);
const busyMessageChanged = computed(
  () => busyMessage.value.trim() !== (props.inbox.call_busy_message || '')
);

watch(
  () => props.inbox.call_busy_message,
  value => {
    busyMessage.value = value || '';
  },
  { immediate: true }
);

const checks = computed(() => {
  const data = health.value;
  if (!data) return [];
  const supported = data.reachable && data.calls_supported;
  return [
    { key: 'REACHABLE', ok: data.reachable, detail: data.error },
    { key: 'SUPPORTED', ok: supported },
    { key: 'SERVER_ENABLED', ok: supported && data.calls_enabled },
    { key: 'ENGINE', ok: data.engine === 'GOWS', detail: data.engine },
    {
      key: 'SESSION',
      ok: data.session_status === 'WORKING',
      detail: data.session_status,
    },
  ];
});
const failedChecks = computed(() => checks.value.filter(check => !check.ok));
const isReady = computed(
  () => checks.value.length > 0 && failedChecks.value.length === 0
);
const serverRecordingOff = computed(
  () => health.value?.calls_enabled && !health.value?.recording
);

const checkHealth = async () => {
  isChecking.value = true;
  try {
    health.value = await WahaCallsAPI.health(props.inbox.id);
  } catch (error) {
    health.value = { reachable: false, error: error.message };
  } finally {
    isChecking.value = false;
  }
};

const refreshInbox = () => store.dispatch('inboxes/get', props.inbox.id);

const toggleVoice = async value => {
  if (isToggling.value || !sessionId.value) return;
  isToggling.value = true;
  try {
    await WahaSessionsAPI.updateConfig(sessionId.value, {
      voice_calls_enabled: value,
    });
    await refreshInbox();
    useAlert(t('INBOX_MGMT.EDIT.API.SUCCESS_MESSAGE'));
  } catch (_) {
    useAlert(t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
  } finally {
    isToggling.value = false;
  }
};

const saveBusyMessage = async message => {
  isSavingBusyMessage.value = true;
  try {
    await InboxesAPI.setCallBusyMessage(props.inbox.id, message);
    await refreshInbox();
    useAlert(t('INBOX_MGMT.EDIT.API.SUCCESS_MESSAGE'));
  } catch (_) {
    useAlert(t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
  } finally {
    isSavingBusyMessage.value = false;
  }
};

const toggleBusyMessage = value =>
  saveBusyMessage(value ? t('INBOX_MGMT.WAHA_CALLING.BUSY.DEFAULT') : '');

onMounted(checkHealth);
</script>

<template>
  <div class="flex flex-col gap-6 pb-8">
    <div
      class="relative"
      :class="{ 'pointer-events-none opacity-60': isToggling }"
    >
      <SettingsToggleSection
        :model-value="voiceEnabled"
        :header="$t('INBOX_MGMT.WAHA_CALLING.ENABLE.LABEL')"
        :description="$t('INBOX_MGMT.WAHA_CALLING.ENABLE.DESCRIPTION')"
        :hide-toggle="isToggling"
        @update:model-value="toggleVoice"
      >
        <template v-if="isToggling" #hiddenToggle>
          <Spinner class="size-4 text-n-slate-11" />
        </template>
      </SettingsToggleSection>
    </div>

    <div
      class="flex flex-col rounded-xl outline outline-1 -outline-offset-1 outline-n-weak"
    >
      <div class="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
        <div class="flex flex-col gap-1 min-w-0">
          <div class="flex items-center gap-2">
            <span class="text-heading-3 text-n-slate-12">
              {{ $t('INBOX_MGMT.WAHA_CALLING.HEALTH.TITLE') }}
            </span>
            <span
              v-if="isReady"
              class="rounded-md bg-n-teal-3 px-1.5 py-0.5 text-xs text-n-teal-11"
            >
              {{ $t('INBOX_MGMT.WAHA_CALLING.HEALTH.READY') }}
            </span>
            <span
              v-else-if="failedChecks.length"
              class="rounded-md bg-n-ruby-3 px-1.5 py-0.5 text-xs text-n-ruby-11"
            >
              {{
                $t('INBOX_MGMT.WAHA_CALLING.HEALTH.ISSUES', {
                  count: failedChecks.length,
                })
              }}
            </span>
          </div>
          <span class="text-body-main text-n-slate-11">
            {{ $t('INBOX_MGMT.WAHA_CALLING.HEALTH.DESCRIPTION') }}
          </span>
        </div>
        <NextButton
          sm
          faded
          slate
          icon="i-lucide-refresh-cw"
          :is-loading="isChecking"
          :label="$t('INBOX_MGMT.WAHA_CALLING.HEALTH.CHECK')"
          @click="checkHealth"
        />
      </div>

      <div
        v-if="isChecking && !health"
        class="flex items-center gap-2 px-4 py-3 border-t border-n-weak text-sm text-n-slate-11"
      >
        <Spinner class="size-4" />
        {{ $t('INBOX_MGMT.WAHA_CALLING.HEALTH.CHECKING') }}
      </div>
      <ul
        v-else
        class="flex flex-col divide-y divide-n-weak border-t border-n-weak"
      >
        <li
          v-for="check in checks"
          :key="check.key"
          class="flex items-start gap-3 px-4 py-2.5"
        >
          <Icon
            :icon="check.ok ? 'i-lucide-circle-check' : 'i-lucide-circle-x'"
            class="size-4 shrink-0 mt-0.5"
            :class="check.ok ? 'text-n-teal-10' : 'text-n-ruby-10'"
          />
          <div class="flex flex-col min-w-0">
            <span class="text-sm text-n-slate-12">
              {{ $t(`INBOX_MGMT.WAHA_CALLING.HEALTH.CHECKS.${check.key}`) }}
              <span v-if="check.detail" class="ml-1 text-n-slate-11">
                {{ check.detail }}
              </span>
            </span>
            <span v-if="!check.ok" class="text-xs text-n-slate-11">
              {{ $t(`INBOX_MGMT.WAHA_CALLING.HEALTH.FIX.${check.key}`) }}
            </span>
          </div>
        </li>
      </ul>
    </div>

    <template v-if="voiceEnabled">
      <div
        class="relative"
        :class="{ 'pointer-events-none opacity-60': isSavingBusyMessage }"
      >
        <SettingsToggleSection
          :model-value="busyMessageEnabled"
          :header="$t('INBOX_MGMT.WAHA_CALLING.BUSY.LABEL')"
          :description="$t('INBOX_MGMT.WAHA_CALLING.BUSY.DESCRIPTION')"
          @update:model-value="toggleBusyMessage"
        >
          <template v-if="busyMessageEnabled" #editor>
            <div class="flex flex-col gap-3">
              <TextArea
                v-model="busyMessage"
                :placeholder="$t('INBOX_MGMT.WAHA_CALLING.BUSY.PLACEHOLDER')"
                auto-height
                resize
              />
              <div>
                <NextButton
                  sm
                  :disabled="!busyMessageChanged || !busyMessage.trim()"
                  :is-loading="isSavingBusyMessage"
                  :label="$t('INBOX_MGMT.SETTINGS_POPUP.UPDATE')"
                  @click="saveBusyMessage(busyMessage.trim())"
                />
              </div>
            </div>
          </template>
        </SettingsToggleSection>
      </div>

      <CallRecordingSettings :inbox="inbox" />
      <p
        v-if="serverRecordingOff"
        class="-mt-3 rounded-xl bg-n-amber-2 px-4 py-3 text-sm text-n-amber-11"
      >
        {{ $t('INBOX_MGMT.WAHA_CALLING.SERVER_RECORDING_OFF') }}
      </p>
    </template>
  </div>
</template>
